import React from 'react';

export type ToastType = 'success' | 'error' | 'warning' | 'info';

export interface ToastItem {
  id: string;
  type: ToastType;
  content: React.ReactNode;
  duration: number;
}

// Errors get a bit longer on screen than a success/info blip -- there's
// usually more to read. Overridable per call (see toastMessage.ts's
// `duration` option, in seconds, same convention antd's own message API used).
export const TOAST_DEFAULT_DURATIONS: Record<ToastType, number> = {
  success: 3000,
  info: 3500,
  warning: 4500,
  error: 5000,
};

// Plain module-level store (no React context) so this is callable from
// anywhere -- a service file, an event handler, deep inside a helper --
// exactly like antd's own `message` static API was. ToastContainer is the
// one place that reads it, via useSyncExternalStore.
let toasts: ToastItem[] = [];
const listeners = new Set<() => void>();
let counter = 0;

const emit = () => listeners.forEach((listener) => listener());

export const subscribeToasts = (listener: () => void): (() => void) => {
  listeners.add(listener);
  return () => listeners.delete(listener);
};

export const getToastsSnapshot = (): ToastItem[] => toasts;

export const pushToast = (type: ToastType, content: React.ReactNode, duration: number): string => {
  const id = `toast-${++counter}`;
  toasts = [...toasts, { id, type, content, duration }];
  emit();
  return id;
};

export const dismissToast = (id: string): void => {
  toasts = toasts.filter((toast) => toast.id !== id);
  emit();
};
